import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { useTheme } from '@material-ui/core/styles';
import Translator from '@/app/helpers/TranslationHelper';
import { restGetGroups } from '@/app/services/groups';

interface OptionItem {
    label: string;
    value: any;
}

interface AsyncFilterSelectProps {
    name?: string;
    placeholder?: string;
    value?: any;
    clearable?: boolean;
    required?: boolean;
    selectedOptionId?: string | number;
    optionKey?: string;
    onOptionSelect: (option: any) => void;
}

const setAvailableOptions = (data: any[]): OptionItem[] =>
    data.map((item) => ({
        label: item.name,
        value: item,
    }));

const findSelectedOption = (
    availableOptions: OptionItem[] | undefined,
    currentOption: any,
    clearable: boolean,
    selectedOptionId?: string | number,
    optionKey: string = 'id'
): any => {
    let selectedOption =
        currentOption ||
        (
            (availableOptions &&
                availableOptions.find(
                    (item) => item.value[optionKey] === selectedOptionId
                )) || { value: undefined }
        ).value;

    if (!clearable && availableOptions && availableOptions.length === 1) {
        selectedOption = availableOptions[0].value;
    }

    return selectedOption;
};

const AsyncFilterSelect: React.FC<AsyncFilterSelectProps> = ({
    name,
    placeholder,
    value = undefined,
    clearable = true,
    required,
    selectedOptionId,
    optionKey = 'id',
    onOptionSelect,
}) => {
    const [option, setOption] = useState<any>(undefined);
    const [availableOptions, setAvailableOptions_] = useState<
        OptionItem[] | undefined
    >(undefined);
    const isMounted = useRef(false);
    const availableOptionsRef = useRef(availableOptions);
    const optionRef = useRef(option);

    availableOptionsRef.current = availableOptions;
    optionRef.current = option;

    useEffect(() => {
        isMounted.current = true;
        restGetGroups().then((data) => {
            if (isMounted.current) {
                const options = setAvailableOptions(data);
                setAvailableOptions_(options);
                const selected = findSelectedOption(
                    options,
                    undefined,
                    clearable,
                    selectedOptionId,
                    optionKey
                );
                setOption(selected);
            }
        });
        return () => {
            isMounted.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const selected = findSelectedOption(
            availableOptionsRef.current,
            optionRef.current,
            clearable,
            selectedOptionId,
            optionKey
        );
        setOption(selected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOptionId]);

    const handleChange = (data: OptionItem | null) => {
        const newOption = data ? data.value : undefined;
        setOption(newOption);
        onOptionSelect(newOption);
    };

    const disabled =
        availableOptions === undefined ||
        (!clearable ? availableOptions.length === 1 : false);

    const theme = useTheme();
    const fontColor = '#A6A5A6';
    const selectStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body1.fontSize,
        fontStyle: 'normal' as const,
        color: fontColor,
    };

    return (
        <Select
            className="header-filter--select"
            style={selectStyle}
            name={name}
            clearable={clearable}
            required={required}
            disabled={disabled}
            isLoading={availableOptions === undefined}
            defaultValue={value}
            value={option}
            valueRenderer={(val: any) => val.name}
            placeholder={
                <span
                    style={{
                        ...selectStyle,
                        color: fontColor,
                        textAlign: 'left' as const,
                        display: 'block',
                    }}
                >
                    {placeholder}
                </span>
            }
            options={availableOptions}
            // @ts-ignore - 1:1 from AsyncFilterSelect, react-select v1 API props
            onChange={handleChange}
            noResultsText={Translator.translate('No results found')}
            clearAllText={Translator.translate('Clear all')}
            clearValueText={Translator.translate('Clear')}
        />
    );
};

export default AsyncFilterSelect;
